import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { digitalOcean } from "@/integrations/digitalocean/client";
import { useQuery } from "@tanstack/react-query";
import { createPageUrl } from "@/utils";
import { usePlayer } from "@/contexts/PlayerContext";
import { Music, Heart, Play, TrendingUp, AlertTriangle, MessageSquare } from "lucide-react";
import SendMessageButton from "@/components/SendMessageButton";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { getDefaultCoverImage } from "@/utils/defaultCoverImage";

export default function FreshDopeSounds() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const { playTrack, currentTrack, isPlaying, togglePlayPause } = usePlayer();

  useEffect(() => {
    document.title = "Fresh Tracks - Soundope";
    loadUser();
  }, []);

  const normalizeCover = (url, id) => {
    if (!url) return getDefaultCoverImage(id);
    let normalized = url.replace(/^http:\/\//i, 'https://');
    normalized = normalized.replace('https://soundope.com/uploads/', 'https://soundope.com/api/uploads/');
    return normalized;
  };

  const loadUser = async () => {
    try {
      const { data: sessionResult } = await digitalOcean.auth.getSession();
      const session = sessionResult?.session;
      if (!session || !session.user) {
        setCurrentUser(null);
        return;
      }

      const { data: profile, error } = await digitalOcean
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();
      if (!error && profile) {
        setCurrentUser(profile);
      }
    } catch (error) {
      console.error("Error loading user:", error);
    }
  };

  const { data: freshTracks = [], isLoading, error: tracksError, refetch } = useQuery({
    queryKey: ["fresh-tracks"],
    queryFn: async () => {
      console.log("[FreshDopeSounds] Fetching tracks from database...");
      const { data, error } = await digitalOcean
        .from("tracks")
        .select("*")
        .eq("is_active", true)
        .order("created_at", { ascending: false })
        .limit(50);
      
      if (error) {
        console.error("[FreshDopeSounds] Error fetching tracks:", error);
        console.error("[FreshDopeSounds] Error details:", {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        toast.error(`Failed to load tracks: ${error.message || "Unknown error"}`);
        throw error;
      }
      
      console.log(`[FreshDopeSounds] Successfully fetched ${data?.length || 0} tracks`);
      return data || [];
    },
    retry: 2, // Retry failed requests twice
  });

  useEffect(() => {
    // Load first track but don't auto-play on page load
    if (freshTracks.length > 0 && !currentTrack) {
      playTrack(freshTracks[0], freshTracks, 0, true, false); // startPlaying = false
    }
  }, [freshTracks]);

  const handlePlayTrack = (track, index) => {
    if (currentTrack && currentTrack.id === track.id) {
      togglePlayPause();
      return;
    }
    // Start playing immediately when user clicks a new track
    playTrack(track, freshTracks, index, true, true); // startPlaying = true
  };

  if (tracksError && !isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen p-8">
        <div className="text-center max-w-md">
          <div className="neuro-pressed w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-[#a0a0a0]" aria-hidden="true" />
          </div>
          <h2 className="text-2xl font-light text-[#d0d0d0] mb-2">Unable to load fresh tracks</h2>
          <p className="text-sm text-[#808080] mb-6" aria-live="polite">
            Something went wrong while fetching the newest drops. Please try again.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => refetch()}
                    className="neuro-base active:neuro-pressed rounded-2xl px-6 py-3"
                  >
                    Retry
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Retry loading the latest tracks</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => navigate(-1)}
                    className="neuro-flat rounded-2xl px-6 py-3"
                  >
                    Go Back
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Go back to the previous page</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => window.location.reload()}
                    className="neuro-flat rounded-2xl px-6 py-3"
                  >
                    Reload
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reload this page</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#808080]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-8 pb-32">
      <div className="max-w-2xl mx-auto">
        <div className="neuro-base rounded-3xl p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="neuro-pressed w-16 h-16 rounded-2xl flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-[#a0a0a0]" />
              </div>
              <div>
                <h1 className="text-2xl md:text-4xl lg:text-5xl font-light text-[#d0d0d0]">Fresh Tracks</h1>
                <p className="text-sm md:text-base text-[#808080]">The 50 newest tracks just dropped</p>
              </div>
            </div>
          </div>

          {freshTracks.length === 0 ? (
            <div className="text-center py-12">
              <Music className="w-16 h-16 mx-auto text-[#606060] mb-4" />
              <p className="text-[#808080] mb-4">No fresh tracks yet</p>
              <Button
                onClick={() => navigate(createPageUrl("Discover"))}
                className="neuro-base active:neuro-pressed rounded-2xl px-6 py-3"
                tooltip="Browse all tracks on the discover page"
              >
                <span className="text-[#a0a0a0]">Browse All Tracks</span>
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
                {freshTracks.map((track, index) => (
                  <TooltipProvider key={track.id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => handlePlayTrack(track, index)}
                          className="neuro-flat rounded-2xl overflow-hidden smooth-transition hover:scale-[1.02] text-left"
                        >
                  <div className="relative">
                    <img
                      src={normalizeCover(track.cover_image_url, track.id)}
                      alt={track.title}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getDefaultCoverImage(track.id);
                      }}
                    />
                    <div className="absolute top-3 left-3 neuro-pressed px-3 py-1 rounded-xl">
                      <span className="text-xs text-[#a0a0a0] font-medium">#{index + 1}</span>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-[#d0d0d0] truncate">
                          {track.title}
                        </h3>
                        <p className="text-xs text-[#808080]">{track.artist_name}</p>
                      </div>
                      {track.artist_id && currentUser && track.artist_id !== currentUser.id && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <SendMessageButton
                            recipientId={track.artist_id}
                            recipientName={track.artist_name}
                            currentUser={currentUser}
                            trackTitle={track.title}
                          />
                        </div>
                      )}
                    </div>
                    
                    {track.genres && track.genres.length > 0 && (
                      <div className="flex gap-2 mb-3">
                        {track.genres.slice(0, 2).map((genre, idx) => (
                          <span
                            key={idx}
                            className="text-xs px-2 py-1 rounded-lg bg-[#1a1a1a] text-[#808080]"
                          >
                            {genre}
                          </span>
                        ))}
                      </div>
                    )}
                    
                  </div>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{currentTrack && currentTrack.id === track.id && isPlaying ? "Pause track" : "Play track"}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
